<template>
  <div class="filter-container" ref="filterContainer" :style="{ maxWidth: chipWidth }">
    <div v-for="(filter, index) in requiredFilters" :key="filter.label">
      <v-tooltip location="bottom">
        <template #activator="{ props }">
          <v-chip
            v-bind="props"
            class="filter-chip"
            variant="outlined"
            :color="isActive(filter.label) ? undefined : undefined" 
            :style="chipStyle(filter)"
            @click="toggleFilter(filter.label)"
            label
          >
            <v-icon size="18" class="mr-1">{{ filter.icon }}</v-icon>
            <!-- show label text next to icon; remove if you want icons-only -->
           
          </v-chip>
        </template>

        <!-- Custom tooltip text for lab-cases -->
        {{ filter.label === 'lab-cases' ? 'Lab Cases (Received -> Not Scheduled)' : filter.label }}
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
export default {
  name: "SchedulingFilter",
  props: {
    modelValue: { type: Array, default: () => [] },
     disabled: { type: Boolean, default: false }
},
  data() {
    return {
      // Non-OD static filters always shown first
      staticFilters: [
        { label: "Unattended", icon: "mdi-alert-circle-outline", color: "#FF2929" },
        { label: "Hot Leads",  icon: "mdi-account-outline",      color: "#FF5722" }
      ]
    };
  },
  computed: {
    ...mapGetters("window", ["isMobile", "isTablet"]),
    ...mapGetters("frontdesk", ["odFilterList"]),
    requiredFilters() {
      const odChips = (this.odFilterList || [])
        .filter(f => f.is_enabled !== 0)
        .slice()
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map(f => ({
          label: f.filter_name,
          icon:  `mdi-${f.icon}`,
          color: f.color
        }));
      return [...this.staticFilters, ...odChips];
    },
    chipWidth() {
        if (this.isMobile) return "100%";
        if (this.isTablet) return "90%";
        return "70%";
    },
    activeFilters: {
      get() {
        // always return an array copy to avoid accidental mutation of parent's array
        return Array.isArray(this.modelValue) ? [...this.modelValue] : [];
      },
      set(value) {
        // emit new array to parent (v-model)
        this.$emit("update:modelValue", Array.isArray(value) ? [...value] : []);
      }
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        // optional debug
        // console.log('SchedulingFilter: Parent updated filters to', newValue);
      }
    }
  },
  methods: {
    toggleFilter(filterLabel) {
        if (this.disabled) return; 
      // SINGLE-SELECTION behavior (current)
      let removedFilter = null;
      let newFilters = [];

      const current = this.activeFilters || [];

      if (!current.includes(filterLabel)) {
        if (current.length > 0) {
          removedFilter = current[0];
        }
        newFilters = [filterLabel];
      } else {
        removedFilter = filterLabel;
        newFilters = [];
      }

      // update via computed setter (emits to parent)
      this.activeFilters = newFilters;

      // additional events
      this.$emit("filterChange", newFilters);
      if (removedFilter !== null) {
        this.$emit("filterRemoved", removedFilter);
      }
    },

    // --- If you wanted MULTI-SELECTION instead, use this:
    // toggleFilterMulti(filterLabel) {
    //   const current = this.activeFilters || [];
    //   const idx = current.indexOf(filterLabel);
    //   let newFilters = [];
    //   if (idx === -1) {
    //     newFilters = [...current, filterLabel];
    //   } else {
    //     newFilters = current.filter(f => f !== filterLabel);
    //   }
    //   this.activeFilters = newFilters;
    //   this.$emit('filterChange', newFilters);
    // },

    isActive(filterLabel) {
      const arr = this.activeFilters || [];
      return arr.includes(filterLabel);
    },

    chipStyle(filter) {
      const active = this.isActive(filter.label);
      return {
        border: active ? `2px solid ${filter.color}` : "1px solid #BDBDBD",
        color: active ? filter.color : "#757575",
        maxWidth: "120px",          // allow more room for the label if you render it
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        background: "transparent"
      };
    },

    onScroll(event) {
      // safer trackpad/mouse detection: treat small delta as trackpad; large delta as wheel
      const container = this.$refs.filterContainer;
      if (!container) return;

      const deltaY = event.deltaY ?? 0;

      // Move horizontally by deltaY
      container.scrollLeft += deltaY;

      // Prevent vertical scrolling only for typical mouse wheels (large, discrete steps)
      const isLikelyMouseWheel = Math.abs(deltaY) > 50 || event.deltaMode === 1;
      if (isLikelyMouseWheel) {
        event.preventDefault();
      }
    }
  },
  mounted() {
    const container = this.$refs.filterContainer;
    if (container) {
      container.addEventListener("wheel", this.onScroll, { passive: false });
    }
  },
  beforeUnmount() {
    const container = this.$refs.filterContainer;
    if (container) {
      container.removeEventListener("wheel", this.onScroll);
    }
  }
};
</script>

<style scoped>
.filter-container {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  padding: 2px;
  gap: 4px;
  white-space: nowrap;
  scrollbar-width: none; 
  -ms-overflow-style: none;
  scroll-behavior: smooth;
}

.filter-container::-webkit-scrollbar {
  display: none; 
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  margin: 2px;
  background: transparent !important;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  border-radius: 8px;
}

.filter-chip:hover {
  transform: scale(1.03);
}

.chip-text {
  margin-left: 6px;
  font-size: 13px;
  line-height: 18px;
}
</style>
